import { useState } from 'react'
import { labelDe, parseOnde } from '@domain/product'
import { EscolherAutor } from '@ui/shell/EscolherAutor'
import type { Merchant, Product, RecordedByKind } from '@domain/product'
import { parseAmountInput } from '@shared/money/parse'
import { todayInHousehold } from '@shared/date/timezone'
import { useAuth } from '@ui/auth/auth-context'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { Botao } from '@ui/glass/Botao'

/**
 * Registrar um preço visto.
 *
 * ## Um componente, e não dois formulários iguais
 *
 * A lista de produtos e o detalhe do produto pedem a mesma coisa. Duas cópias
 * divergiriam no primeiro ajuste — uma ganharia o campo "onde" e a outra não —,
 * que é a mesma razão de `ComparacaoDeFamilia` ter sido extraída.
 *
 * ## Quem registrou é uma ESCOLHA, e o padrão é quem está logado
 *
 * *"Escolhe o usuário que está logado ou pode ser um agente de IA (...) mas não
 * precisa ser necessariamente um usuário dentro do sistema."* Três opções, e o
 * caminho comum — sou eu — não custa nenhum toque a mais.
 *
 * O **nome** só aparece quando não é membro: pedi-lo para quem está logado seria
 * pedir o que já se sabe.
 */
export function RegistrarPreco({
  produto,
  lojas,
  onGravado,
  onCancelar,
}: {
  produto: Product
  /** Para sugerir as que já existem, e não criar a mesma duas vezes. */
  lojas: Merchant[]
  onGravado: () => void
  onCancelar: () => void
}) {
  const { productRepository } = useFinanceRepositories()
  const { session } = useAuth()

  const [preco, setPreco] = useState('')
  const [onde, setOnde] = useState('')
  const [quem, setQuem] = useState<RecordedByKind>('user')
  const [nome, setNome] = useState('')
  const [nota, setNota] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const doUsuario = session.status === 'ready' ? session.user.displayName : null

  const gravar = () => {
    const parsed = parseAmountInput(preco)
    if (parsed.status !== 'ok' || Math.abs(parsed.cents) === 0) {
      setErro('informe o preço')
      return
    }
    setErro(null)
    setSalvando(true)
    void productRepository
      .recordPrice({
        productId: produto.id,
        totalCents: Math.abs(parsed.cents),
        quantity: 1,
        where: onde.trim() === '' ? null : onde.trim(),
        observedAt: todayInHousehold(),
        recordedBy: { kind: quem, label: labelDe(quem, nome, doUsuario) },
        note: nota.trim() === '' ? null : nota.trim(),
        supersedesId: null,
      })
      .then(onGravado)
      .catch((e: unknown) => {
        console.error('RegistrarPreco: não deu para gravar', e)
        // Produto sem tamanho é o caso ESPERADO, e não uma falha genérica: a
        // porta recusa em vez de inventar uma unidade base, e a tela diz o que
        // falta. Perder essa distinção foi o que a suíte pegou quando este
        // formulário virou componente.
        setErro(
          produto.size === null
            ? 'este produto não tem tamanho declarado, então não dá para comparar preço'
            : 'não foi possível salvar agora',
        )
      })
      .finally(() => {
        setSalvando(false)
      })
  }

  return (
    <div>
      <p className="mb-2 text-sm">{`Preço visto de ${produto.name}`}</p>

      <label>
        <span className="sr-only">Preço</span>
        <input
          inputMode="decimal"
          value={preco}
          aria-label="Preço"
          placeholder="0,00"
          className="w-28 rounded-lg border border-[var(--border)] bg-transparent px-2 py-1 text-right"
          onChange={(e) => {
            setPreco(e.target.value)
          }}
        />
      </label>

      {/* Um campo só para lugar e link — são a mesma pergunta. Ver
          `merchant.ts`. */}
      <label className="mt-3 block text-sm">
        <span className="block text-xs text-[var(--text-muted)]">
          Onde (opcional) — um lugar ou um link
        </span>
        <input
          value={onde}
          aria-label="Onde"
          list="lojas-conhecidas"
          placeholder="Walmart Campo Comprido, ou o link do produto"
          className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
          onChange={(e) => {
            setOnde(e.target.value)
          }}
        />
        <datalist id="lojas-conhecidas">
          {lojas.map((l) => (
            <option key={l.id} value={l.name} />
          ))}
        </datalist>
        {onde.trim() !== '' && (
          <span className="mt-1 block text-xs text-[var(--text-muted)]">
            {parseOnde(onde).sourceUrl === null
              ? `Vai como lugar: ${parseOnde(onde).merchantName ?? ''}`
              : `Vai como link, na loja ${parseOnde(onde).merchantName ?? ''}`}
          </span>
        )}
      </label>

      <div className="mt-3">
        <EscolherAutor
          legenda="Quem encontrou"
          quem={quem}
          nome={nome}
          doUsuario={doUsuario}
          onQuem={setQuem}
          onNome={setNome}
        />
      </div>

      <label className="mt-3 block text-sm">
        <span className="block text-xs text-[var(--text-muted)]">Anotação (opcional)</span>
        <input
          value={nota}
          aria-label="Anotação"
          placeholder="promoção do dia, leva 3 paga 2…"
          className="w-full rounded-lg border border-[var(--border)] bg-transparent px-2 py-1"
          onChange={(e) => {
            setNota(e.target.value)
          }}
        />
      </label>

      {erro !== null && (
        <p role="alert" className="mt-2 text-sm">
          {erro}
        </p>
      )}

      <div className="mt-3 flex gap-3">
        <Botao
          tom="primario"
          type="button"
          disabled={salvando}
          onClick={gravar}
        >
          {salvando ? 'Salvando…' : 'Salvar preço'}
        </Botao>
        <Botao type="button" onClick={onCancelar}>
          Cancelar
        </Botao>
      </div>

      <p className="mt-2 text-xs text-[var(--text-muted)]">
        Isto é um preço <strong>visto</strong>, não pago: ele entra no menor preço encontrado, e
        fica fora da média do que a casa gastou.
      </p>
    </div>
  )
}
